// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors } from '@toggl/style'
import { Button } from '@toggl/ui'

import * as text from '../text'

const buttonTypes = {
  primary: Button.Primary,
  secondary: Button.Secondary,
  tertiary: Button.Tertiary,
}

type Props = {
  className?: string,
  title: React.Node | string,
  titleSize?: 1 | 2,
  subTitle?: React.Node,
  signUpPoints?: string[],
  cta?:
    | React.Node
    | Array<{
        link: string,
        type: 'primary' | 'secondary' | 'tertiary',
        text: string,
      }>,
  titleColor?: string,
  align?: string,
  subText?: string,
}

export const ContainerSectionTitle = ({
  className,
  cta,
  title,
  titleColor,
  titleSize = 1,
  subTitle,
  signUpPoints,
  subText,
  align,
}: Props) => {
  const Header = titleSize === 1 ? H2 : H3
  const isTitleText = typeof title === 'string'

  const renderCta = () => {
    if (Array.isArray(cta)) {
      return cta.map(({ link, type, text }, index) => {
        const ButtonComponent = buttonTypes[type] || Button.Primary
        return (
          <ButtonComponent
            key={index}
            as={'a'}
            href={link}
            color={colors.peach10}
            caretDirection='right'
          >
            {text}
          </ButtonComponent>
        )
      })
    } else {
      return cta
    }
  }

  return (
    <Root align={align} className={className} cta={!!cta}>
      {subText && (
        <text.UppercaseOneLiner color={colors.darkPurple70}>
          {subText}
        </text.UppercaseOneLiner>
      )}
      {isTitleText ? <Header headingColor={titleColor}>{title}</Header> : title}
      {subTitle}
      {signUpPoints && (
        <SignUpPoints>
          {signUpPoints.map((point, index) => (
            <SUPoint key={index} color={colors.darkPurple20}>
              <img
                src={require('./assets/icon-check-ghost.svg').default}
                width='26'
                alt='Checkmark icon'
              />
              {point}
            </SUPoint>
          ))}
        </SignUpPoints>
      )}

      {cta && <CtaBox align={align}>{renderCta()}</CtaBox>}
    </Root>
  )
}

export const Root: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 800px;
  text-align: ${(p) => p.align || 'center'};
  margin-bottom: ${(p) => (p.cta ? '60px' : '40px')};
  h2 {
    text-wrap: balance;
  }
  p {
    max-width: 650px;
    text-wrap: balance;
  }
  ${(p) =>
    p.align !== 'left' &&
    `
      margin-left: auto;
      margin-right: auto;

      p {
        margin-left: auto;
        margin-right: auto;
      }
      `};
`

const H2 = styled(text.H2)`
  color: ${(p) => p.headingColor || p.theme.primary};
`
const H3 = styled(text.H3)`
  color: ${(p) => p.headingColor || p.theme.primary};
`

const CtaBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  align-items: center;
  ${(p) =>
    p.align === 'left'
      ? `justify-content: flex-start`
      : `justify-content: center`};
  margin-top: 15px;
  ${Button.Primary}, ${Button.Alternate}, button {
    display: inline-flex;
  }
`

const SignUpPoints = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 30px;
  row-gap: 15px;
  margin: 20px auto;
`

const SUPoint = styled(text.P2)`
  display: flex;
  align-items: center;
  gap: 8px;
`
